<!--
  BrainPicker — the "brain" (model) selector for the simple Assistant editor. A tappable list of the
  user's CONFIGURED models (provider logo + name), where the selected value is the model id. Pure
  select-only — mounting a new model is a separate, explicit action (New assistant → Add a new brain).
  Shows a message instead of an empty list when the user has no models yet.
-->
<template>
  <div>
    <v-alert
      v-if="!models.length"
      type="warning"
      variant="tonal"
      density="comfortable"
      icon="mdi-alert-circle-outline"
    >
      Please configure a model first — add one from <strong>New assistant → Add a new brain</strong>.
    </v-alert>

    <div v-else class="d-flex flex-column ga-2">
      <!-- provider filter: tick one or more providers to narrow the brains (empty = all) -->
      <div v-if="providerOptions.length > 1" class="d-flex align-center flex-wrap ga-2 mb-1">
        <span class="text-caption text-medium-emphasis mr-1">Provider:</span>
        <v-checkbox
          v-for="p in providerOptions"
          :key="p.key"
          v-model="selectedProviders"
          :value="p.key"
          :label="p.label"
          density="compact"
          hide-details
          class="provider-check"
        />
      </div>

      <v-card
        v-for="m in visibleModels"
        :key="m.id"
        :variant="isSelected(m) ? 'tonal' : 'outlined'"
        :color="isSelected(m) ? 'primary' : undefined"
        rounded="md"
        class="pa-3 d-flex align-center ga-4 brain-row"
        @click="$emit('update:modelValue', m.id)"
      >
        <v-avatar size="40" color="surface" variant="tonal" rounded="md">
          <v-img v-if="isImgIcon(m.providerKey)" :src="(iconFor(m.providerKey) as ProviderIcon).src" :alt="label(m.providerKey)" />
          <span v-else-if="iconFor(m.providerKey)" class="brain-logo-mask" :style="maskStyle(m.providerKey)" role="img" :aria-label="label(m.providerKey)" />
          <span v-else class="text-caption">{{ initials(m.providerKey) }}</span>
        </v-avatar>

        <div class="flex-grow-1" style="min-width: 0">
          <div class="text-subtitle-1 font-weight-bold text-truncate">{{ m.name }}</div>
          <div class="text-caption text-medium-emphasis text-truncate">{{ label(m.providerKey) }} · {{ m.model }}</div>
        </div>

        <v-icon
          :icon="isSelected(m) ? 'mdi-check-circle' : 'mdi-circle-outline'"
          :color="isSelected(m) ? 'primary' : 'medium-emphasis'"
        />
      </v-card>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import { PROVIDER_LABELS } from '@/data/assistants'
import { getProviderIcon, getProviderBrandColor } from '@/utils/providerIcons'
import type { ProviderIcon, ThemeName } from '@/utils/providerIcons'
import type { ModelConfig } from '@/types/models'

@Options({
  props: {
    modelValue: { type: String, default: '' }, // selected model id
    models: { type: Array, default: () => [] } // the user's configured ModelConfigs
  },
  emits: ['update:modelValue'],
  computed: {
    ...mapGetters(['currentTheme'])
  }
})
export default class BrainPicker extends Vue {
  modelValue!: string
  models!: ModelConfig[]
  currentTheme!: string

  // Provider keys the user has ticked to narrow the list. Empty = no filter (show every brain).
  selectedProviders: string[] = []

  // Distinct providers among the available brains, so the checkboxes only show relevant options.
  get providerOptions(): { key: string; label: string }[] {
    const keys = new Set<string>()
    for (const m of this.models ?? []) keys.add(m.providerKey)
    return [...keys].sort().map((key) => ({ key, label: this.label(key) }))
  }

  // The brains actually rendered: narrowed to the ticked providers (empty selection = all).
  get visibleModels(): ModelConfig[] {
    if (!this.selectedProviders.length) return this.models ?? []
    const wanted = new Set(this.selectedProviders)
    return (this.models ?? []).filter((m) => wanted.has(m.providerKey))
  }

  isSelected(m: ModelConfig): boolean {
    return m.id === this.modelValue
  }

  label(p: string): string {
    return PROVIDER_LABELS[p] || p
  }

  initials(p: string): string {
    return this.label(p).slice(0, 2).toUpperCase()
  }

  iconFor(p: string): ProviderIcon | null {
    return getProviderIcon(p, this.currentTheme as ThemeName)
  }

  isImgIcon(p: string): boolean {
    const icon = this.iconFor(p)
    return !!icon && icon.mode !== 'mono'
  }

  maskStyle(p: string): Record<string, string> {
    const icon = this.iconFor(p)
    if (!icon) return {}
    const url = `url("${icon.src}")`
    return {
      '-webkit-mask-image': url,
      'mask-image': url,
      backgroundColor: icon.tint || getProviderBrandColor(p)
    }
  }
}
</script>

<style scoped lang="scss">
.brain-row {
  cursor: pointer;
}
// Keep each provider checkbox tight so several sit on one row instead of stretching full width.
.provider-check {
  flex: 0 0 auto;
}
.brain-logo-mask {
  display: inline-block;
  width: 70%;
  height: 70%;
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}
</style>
